import React, { useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import * as pdfjsLib from "pdfjs-dist";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Endpoints } from "../../Global/Endpoints";
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import ErrorModal from "../Security/ErrorModal";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url
).toString();

const MultiSelectForm = () => {
  const [formData, setFormData] = useState({
    journey: [],
    identifyas: [],
    briefbuildjourney: "",
    doctor: "",
    advicetobuildjourney: "",
    comfortable: [],
  });
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const token = localStorage.getItem("AccessToken");
  const [errors, setErrors] = useState({});
  const contract = useSelector((state) => state.userData);
  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const [documentType, setDocumentType] = useState("WINFamilyPhoto");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null); // Add ref for file input
  const { t } = useTranslation();

  const getPageCount = (file) => {
      return new Promise(async (resolve, reject) => {
          const bytes = await file.arrayBuffer()
          
          const loadingTask = pdfjsLib.getDocument({
              data: bytes,
              disableWorker: true});
            try {
              const pdf = await loadingTask.promise;
              const pages = pdf.numPages;
              resolve(pages);
            } catch (error) {
              reject(error);
          }
      });
  };

  const journeyOptions = [
    t('winfertility'),
    t('winadoption'),
    t('winbehave'),
    t('winmeno'),
    t('wincare'),
    t('windoula'),
    t('winegg'),
    t('wingeneric'),
    t('winsurogacy'),
    t('winparenting'),
    t('winmaternity'),
  ];

  const identifyAsOptions = [
    t('winlgb'),
    t('winover'),
    t('wintrans'),
    t('winbipoc'),
    t('winsolo'),
    t('winnone'),
  ];

  const comfortableOptions = [
    t('winsharing'),
    t('winpr'),
    t('winnone'),
  ];

  const handleCheckboxChange = (key, value) => {
    setFormData((prev) => {
      const selected = prev[key];
      if (selected.includes(value)) {
        return { ...prev, [key]: selected.filter((item) => item !== value) };
      } else {
        if ((key === "comfortable" || key === "identifyas") && value === "None of the above") {
          return { ...prev, [key]: [value] };
        } else if ((key === "comfortable" || key === "identifyas") && selected.includes("None of the above")) {
          return { ...prev, [key]: selected.filter((item) => item !== "None of the above").concat(value) };
        } else {
          return { ...prev, [key]: [...selected, value] };
        }
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.journey.length) newErrors.journey =t('winvaljour');
    if (!formData.identifyas.length) newErrors.identifyas = t('winvalidenty');
    if(!formData.briefbuildjourney.trim()) newErrors.briefbuildjourney=t('winvalbuild');
    if(!formData.doctor.trim()) newErrors.doctor=t('winvaldoct');

    setErrors((prev) => ({ ...prev, ...newErrors })); // Merge with existing file errors
    return Object.keys(newErrors).length === 0;
  };

 const handleFileSelection = (event) => {
  const files = Array.from(event.target.files);
  let oversizedFileName = "";
  let duplicateFound = false;
  let hasInvalidType = false; // 👈 added
  const allowedTypes = ["image/jpeg", "image/png"];

  if ((files.length + selectedFiles.length) > 4) {
    toast.error("Maximum 4 files are allowed");
    if (fileInputRef.current) fileInputRef.current.value = "";
    return;
  }

  // Check for duplicates with already selected files
  for (const file of files) {
    const isDuplicate = selectedFiles.some(
      f => f.name === file.name && f.size === file.size
    );
    if (isDuplicate) {
      duplicateFound = true;
      break;
    }
  }

  // Check for duplicates within the new files themselves (if multiple selected at once)
  const fileSignatures = new Set();
  for (const file of files) {
    const signature = `${file.name}_${file.size}`;
    if (fileSignatures.has(signature)) {
      duplicateFound = true;
      break;
    }
    fileSignatures.add(signature);
  }

  const validFiles = files.filter(file => {
    if (!allowedTypes.includes(file.type)) {   // 👈 added
      hasInvalidType = true;                   // 👈 added
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      oversizedFileName = file.name;
      return false;
    }
    return true;
  });

  if (duplicateFound) {
    setErrors((prev) => ({
      ...prev,
      file: "Duplicate file(s) not allowed.",
    }));
    if (fileInputRef.current) fileInputRef.current.value = "";
    return;
  }

  if (validFiles.length > 0 && !oversizedFileName) {
    setSelectedFiles(prevFiles => [...prevFiles, ...validFiles]);
    setErrors((prev) => ({ ...prev, file: null }));
  } else if (!duplicateFound && !oversizedFileName) {
    setErrors((prev) => ({
      ...prev,
      file: t('uploadval'),
    }));
  }

  // ✅ Show 5MB error only if there are no invalid-type files
  if (oversizedFileName && !hasInvalidType) {
    toast.error(`The ${oversizedFileName} is too large (over 5MB). Please upload a smaller file.`);
  }

  if (fileInputRef.current) fileInputRef.current.value = "";
};

 // Added handleDelete to remove files before submission
 const handleDelete = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  if (fileInputRef.current) {
    fileInputRef.current.value = "";
  }
  };

  const uploadDocuments = async () => {
    if (selectedFiles.length === 0) return []; // Skip upload if no files selected

    try {
      let fileID = 1;
      const payloads = [];

      for (const file of selectedFiles) {
        let noOfPages = 1;

        if (file.type === "application/pdf") {
          noOfPages = await getPageCount(file);
        }

        const fileToBase64 = (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
          });

        const base64Data = await fileToBase64(file);

        const filePayload = {
          FileID: fileID.toString(),
          FileName: file.name,
          NoOFPages: noOfPages.toString(),
          inputBytes: base64Data,
          fileSize: file.size.toString(),
          memberId: contract.userData.MemberID,
          contract: contract.userData.ContractIdentified,
          docTypeDescription: "WINFamilyPhoto",
          DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
          EmailID: contract.userData.EmailID,
        };

        payloads.push(filePayload);
        fileID++;
      }

      const response = await axios.post(Endpoints.UploadDocument, JSON.stringify(payloads), {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.length) {
        console.log("Documents uploaded successfully:", response.data);
        const totalFiles = payloads.length;
        return { uploadedData: response.data, totalFiles };
      } else {
        console.warn("No file paths returned from upload endpoint.");
        return { uploadedData: [], totalFiles: 0 };
      }
    } catch (error) {
      console.error("Error uploading documents:", error);
      throw error;
    }
  };

  const sendToWin = async (uploadedData, totalFiles) => {
    if (uploadedData.length === 0) return true; // Skip if no documents uploaded
    const payload = {
      PhysicianName:"",
      PhysicianTaxNo:"",
      TotalAmount:0,
      DocumentTypes:"",
      Speciality:"",
      Activity:"",
      ProviderID:"",
      GroupID:"",
      EmailID: contract.userData.EmailID,
      DoctypeDesc: "WINFamilyPhoto",
      Contract: contract.userData.ContractIdentified,
      ReimbursementType:"",
      Documents: uploadedData.map((file) => ({
        NoOFPages: file.NoOFPages,
        FilePath: file.FilePath,
        FileID: file.FileID,
        FileName: file.FileName,
        DocTypeDesc:""
      })),
      ReimbursementDetails: "",
        TotalAmount: 0.0,
        PhysicianName: null,
        PhysicianTelNo:"",
        PhysicianTaxNo:"",
        ReimbursementType:"",
        ServiceType:"",
        AuthNo:"",
      DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
      UserName:"MAPP",
      MemberID: contract.userData.MemberID,
      TotalFiles: totalFiles,
    };
    try {
      const response = await axios.post(Endpoints.SendToWin, payload, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.Result === undefined || Number(response.data?.Result) === 2) {
        setSelectedFiles([]);
        setDocumentType("");
        console.log("Documents sent to WIN successfully.");
        return true;
      }

      setModalMessage(response.data?.ResultText || "Failed to upload documents. Please try again.");
      setShowModal(true);
      return false;
    } catch (error) {
      console.error("Error sending documents to WIN:", error);
      setModalMessage("An error occurred while uploading documents. Please try again.");
      setShowModal(true);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
     // Validate the form excluding file validation
  const isFormValid = validateForm();
  if (!isFormValid || errors.file) return;
    try {
      // Upload documents first
      let uploadedData = [];
      let totalFiles = 0;
      if (selectedFiles) {
        try {
          const uploadResult = await uploadDocuments();
          uploadedData = uploadResult.uploadedData;
          totalFiles = uploadResult.totalFiles;
         
        } catch (uploadError) {
          console.error("Error uploading documents, proceeding without file upload.", uploadError);
        }
       
      }

      // Send uploaded documents to WIN if available
      if (uploadedData.length > 0) {
        try {
          const sendToWinSuccess = await sendToWin(uploadedData, totalFiles);
          if (!sendToWinSuccess) {
            return;
          }
        } catch (sendToWinError) {
          console.error("Error sending documents to WIN.", sendToWinError);
        }
      }
    
      // Save form data
      const params = {
        MemberID: contract.userData.MemberID,
        Contract: contract.userData.ContractIdentified,
        journey: formData.journey.join(", "),
        identifyas: formData.identifyas.join(", "),
        briefbuildjourney: formData.briefbuildjourney,
        doctor: formData.doctor,
        advicetobuildjourney: formData.advicetobuildjourney,
        comfortable: formData.comfortable.join(", "),
      };

      try {
        const response = await axios.post(
          Endpoints.SaveWincentiveStory,
          `_Params=${encodeURIComponent(JSON.stringify(params))}`,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data) {
          //toast.success("Form submitted successfully!");
          setFormData({
            journey: [],
            identifyas: [],
            briefbuildjourney: "",
            doctor: "",
            advicetobuildjourney: "",
            comfortable: [],
          });
            setSelectedFiles(null); // Clear file state
            setDocumentType("");
            setModalMessage (t("formsubmit"));
            setShowModal(true);
          
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Clear file input field
        //navigate('/incentive-landingpage');
      }
        
        }
      } catch (saveError) {
        console.error("Error saving form data.", saveError);
        toast.error("An error occurred while saving form data.");
      }
    } catch (error) {
      console.error("Error during form submission.", error);
      //toast.error("An error occurred during submission.");
    }

  };

  const handleBackClick = () => {
    navigate(-1); 
  };
  const handleModalClose = () => {
    navigate(-1);
  };
  const handleViewFile = (file) => {
  const fileURL = URL.createObjectURL(file);
  window.open(fileURL, "_blank");
};

  return (
    <>
      <ToastContainer />
      <div className="col-md-12 mt-4 inner-page-heading-wrapper">
      <button onClick={handleBackClick} className="win-back-btn">
            <span><i  className="bi bi-chevron-left"></i> {t('back')}</span>
          </button>
        <h2 className="page-heading">{t("WINCentive Story")}</h2>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="row">
        <div className="wincent-form-wrapper col-md-6">
          <h3 className="page-heading-20-black-600">{t("winjourney")}</h3>
          <p className="text-primary-color">{t('winselect')}</p>
          {journeyOptions.map((option) => (
            <label key={option} className="wincentive-checkbox-wrapper">
              <input
                type="checkbox" className="form-check-input radio-btn win-form-check-input wincent-form-checkbox"
                checked={formData.journey.includes(option)}
                onChange={() => handleCheckboxChange("journey", option)}
              />
              {option}
            </label>          
          ))}
          {errors.journey && <p className="form-field-error-message">{errors.journey}</p>}
        </div>

        <div className="wincent-form-wrapper col-md-6">
          <h3 className="page-heading-20-black-600">{t('winidentify')}</h3>
          <p className="text-primary-color">{t('wincheck')}</p>
          {identifyAsOptions.map((option) => (
            <label key={option} className="wincentive-checkbox-wrapper">
              <input
                type="checkbox" className="form-check-input radio-btn win-form-check-input wincent-form-checkbox"
                checked={formData.identifyas.includes(option)}
                onChange={() => handleCheckboxChange("identifyas", option)}
              />
              {option}
            </label>
          ))}
          {errors.identifyas && <p className="form-field-error-message">{errors.identifyas}</p>}
        </div>

        <div className="wincent-form-wrapper col-md-12">
        <h3 className="page-heading-20-black-600 mt-5">{t('winstory')}</h3>
        <p className="text-primary-color">{t('winchallenges')}</p>
        <textarea
          name="briefbuildjourney"
          value={formData.briefbuildjourney}
          onChange={handleInputChange}
          className="win-form-control textarea-control-w-100"
        />
        {errors.briefbuildjourney && <p className="form-field-error-message">{errors.briefbuildjourney}</p>}
        </div>

        <div className="wincent-form-wrapper col-md-12 mt-4">            
              <h3 className="page-heading-20-black-600">{t('windoctor')}</h3>
              <input
                type="text" 
                name="doctor"
                value={formData.doctor}
                onChange={handleInputChange}
                className="win-form-control"
              />              
              {errors.doctor && <p className="form-field-error-message">{errors.doctor}</p>}
        </div>

        <div className="wincent-form-wrapper col-md-12">
          <h3 className="page-heading-20-black-600 mt-5">{t('winquestion')}</h3>
            <ul className="list-style">
              <li>{t('winreward')}</li>
              <li>{t('winrewards')}</li>
              <li>{t('winexperience')}</li>
            </ul>
        </div>

        <div className="wincent-form-wrapper col-md-12">
          <h3 className="page-heading-20-black-600 mt-5">{t('winadvice')}</h3>
          <p className="text-primary-color">{t('winoptional')}</p>
          <textarea
            name="advicetobuildjourney" 
            value={formData.advicetobuildjourney}
            onChange={handleInputChange}
            className="win-form-control textarea-control-w-100"
          />
        </div>
        
        <div className="wincent-form-wrapper col-md-12 mt-5">
          <h3 className="page-heading-20-black-600">{t('wincomfort')}</h3>
          <p className="text-primary-color">{t('wincheck')}{t('winoptional')}</p>
          {comfortableOptions.map((option) => (
            <label key={option} className="wincentive-checkbox-wrapper">
              <input
                type="checkbox" className="form-check-input radio-btn win-form-check-input wincent-form-checkbox"
                checked={formData.comfortable.includes(option)}
                onChange={() => handleCheckboxChange("comfortable", option)}
              />
              {option}
            </label>
          ))}
        </div>
        
        <div className="wincent-form-wrapper col-md-12 mt-5">
          <button
            type="button" className="win-btn-secondary-w-content mobile-view"
            onClick={() => fileInputRef.current?.click()}
          >
            {t('winphoto')}
          </button>
          <div className="text-muted mt-2" style={{ fontSize: "14px" }}>
    Only JPEG, PNG files are allowed to upload.
  </div>
          <input
            type="file"
            multiple
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileSelection}
          />
  <div className="mt-2">
    <table className="table">
      <thead className="winpay-table-header">
        <tr className="winpay-table-header">
          <th width="80%">{t("filenamelable")}</th>
          <th width="140" className="text-center">{t("btnview")}</th>
          <th width="140" className="text-center">{t("btndelete")}</th>
        </tr>
      </thead>
      <tbody>
        {selectedFiles && selectedFiles.length > 0 ? (
          selectedFiles.map((file, index) => (
            <tr className="winpay-table-row" key={index}>
              <td>{file.name}</td>
              <td className="text-center">
                <button
                  className="winpay-table-view-icon mx-auto"
                  type="button"
                  onClick={() => handleViewFile(file)}
                  title={t("btnview")}
                >
                </button>
              </td>
              <td className="text-center">
                <button
                  className="winpay-table-trash-icon mx-auto"
                  type="button"
                  onClick={() => handleDelete(index)}
                  title={t("btndelete")}
                >
                </button>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="3" className="text-center">
              {t('noFilesUploaded')}
            </td>
          </tr>
        )}
      </tbody>
    </table>

            {errors.file && (
              <p className="form-field-error-message">{errors.file}</p>
            )}
          </div>
        </div>

        <div className="wincent-form-wrapper col-md-12 my-5 text-center">
          <button className="win-btn-primary model-popup-btn mx-auto" type="submit">{t('submit')}</button>
        </div>
        </div>
      </form>
      <ErrorModal show={showModal} message={modalMessage} onClose={handleModalClose} closeButton={true} />
    </>
  );
};

export default MultiSelectForm;
